import { Box, Chip, Stack, Typography } from "@mui/material";
import type { RefObject } from "react";
import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";

const PRESET_AMOUNTS = [100, 200, 500, 1000, 2000] as const;

// Digits with an optional separator (dot or the cs/sk comma) and at most 2 decimals.
// Gating on this refuses over-precise input instead of reformatting the committed value.
const AMOUNT_INPUT_PATTERN = /^\d*([.,]\d{0,2})?$/;

type Props<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  /** The form field holding the amount as the raw typed string (see below). */
  name: Path<TFieldValues>;
  label: string;
  currency?: string;
  inputRef?: RefObject<HTMLInputElement | null>;
};

export const AmountField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  currency = "Kč",
  inputRef,
}: Props<TFieldValues>) => {
  const { field, fieldState } = useController({ control, name });

  // The form holds the amount as the raw typed string (a text input, so an in-progress trailing
  // separator like "123." is preserved and drives the auto-width). It is the single source of
  // truth — a form reset clears it — so this field keeps no local state of its own.
  const value = typeof field.value === "string" ? field.value : "";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (!AMOUNT_INPUT_PATTERN.test(raw)) {
      return;
    }
    field.onChange(raw);
  };

  return (
    <Stack alignItems="center" gap={0.5}>
      <Typography fontWeight={400} color="text.secondary">
        {label}
      </Typography>
      <Box display="flex" justifyContent="center" width="100%">
        <Box display="inline-flex" alignItems="baseline">
          <Box
            component="input"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="0"
            value={value}
            onChange={handleChange}
            onBlur={field.onBlur}
            name={field.name}
            ref={(node: HTMLInputElement | null) => {
              field.ref(node);
              if (inputRef) {
                inputRef.current = node;
              }
            }}
            sx={{
              border: "none",
              outline: "none",
              background: "transparent",
              textAlign: "right",
              fontSize: "2rem",
              fontWeight: 700,
              padding: 0,
              width: `${Math.max(1.5, value.length + 0.5)}ch`,
              minWidth: "1.5ch",
              maxWidth: "10ch",
              fontFamily: "inherit",
              color: "text.primary",
              "&::placeholder": {
                color: "text.disabled",
                opacity: 1,
              },
            }}
          />
          <Typography
            fontSize="1.5rem"
            fontWeight={700}
            color="text.secondary"
            ml={1}
            whiteSpace="nowrap"
          >
            {currency}
          </Typography>
        </Box>
      </Box>
      <Stack direction="row" gap={1} justifyContent="center" my={1}>
        {PRESET_AMOUNTS.map((amount) => (
          <Chip
            key={amount}
            label={`${amount} ${currency}`}
            variant={value === String(amount) ? "filled" : "outlined"}
            size="small"
            onClick={() => field.onChange(String(amount))}
          />
        ))}
      </Stack>
      {fieldState.error?.message && (
        <Typography color="error" variant="caption">
          {fieldState.error.message}
        </Typography>
      )}
    </Stack>
  );
};
